import { html, nothing } from "lit";
import type { ChannelAccountSnapshot } from "../types.ts";
import type { ChannelKey, ChannelsProps } from "./channels.types.ts";

export function channelEnabled(key: ChannelKey, props: ChannelsProps) {
  const snapshot = props.snapshot;
  const channels = snapshot?.channels as Record<string, unknown> | null;
  if (!snapshot || !channels) {
    return false;
  }
  const channelStatus = channels[key] as Record<string, unknown> | undefined;
  const configured = typeof channelStatus?.configured === "boolean" && channelStatus.configured;
  const running = typeof channelStatus?.running === "boolean" && channelStatus.running;
  const connected = typeof channelStatus?.connected === "boolean" && channelStatus.connected;
  const accounts = snapshot.channelAccounts?.[key] ?? [];
  const accountActive = accounts.some(
    (account) => account.configured || account.running || account.connected,
  );
  return configured || running || connected || accountActive;
}

export function getChannelAccountCount(
  key: ChannelKey,
  channelAccounts?: Record<string, ChannelAccountSnapshot[]> | null,
): number {
  return channelAccounts?.[key]?.length ?? 0;
}

export function renderChannelAccountCount(
  key: ChannelKey,
  channelAccounts?: Record<string, ChannelAccountSnapshot[]> | null,
) {
  const count = getChannelAccountCount(key, channelAccounts);
  if (count < 2) {
    return nothing;
  }
  return html`<div class="account-count">Accounts (${count})</div>`;
}

/**
 * A single glanceable status chip that replaces stacked "Configured: Yes /
 * Running: Yes" rows. Running → Live (mint), configured-but-idle → Ready
 * (amber), otherwise Not set up (muted).
 */
export function channelStatusChip(s: {
  configured?: boolean | null;
  running?: boolean | null;
  connected?: boolean | null;
}) {
  const live = Boolean(s.running || s.connected);
  const ready = Boolean(s.configured);
  const cls = live ? "live" : ready ? "ready" : "off";
  const text = live ? "Live" : ready ? "Ready" : "Not set up";
  return html`<span class="chan-chip ${cls}"><span class="chan-chip__dot"></span>${text}</span>`;
}

/**
 * A compact one-line meta strip for the diagnostic detail (mode, timestamps)
 * that used to be five stacked rows. Entries whose value is null/"n/a" are
 * dropped so the line only shows what's real.
 */
export function channelMeta(items: Array<{ k: string; v: string | null | undefined }>) {
  const shown = items.filter((i) => i.v && i.v !== "n/a");
  if (shown.length === 0) {
    return nothing;
  }
  return html`<div class="chan-meta">
    ${shown.map(
      (i) => html`<span class="chan-meta__item"><span class="chan-meta__k">${i.k}</span>${i.v}</span>`,
    )}
  </div>`;
}
